/**
 * Sezione Admin «Single Sign-On (Microsoft Entra ID)» — TC_AUTH_PROVIDER=entra.
 *
 * Configura il login Microsoft del portale (oidc-client-ts, client pubblico SPA): tenant, client ID
 * della App registration e mapping gruppi / App Role Entra → ruoli TC. Salvato sul product-service
 * (GET/PUT /admin/sso), letto a runtime dalla LoginPage: nessuna ricompilazione del portale.
 * Solo SYSTEM_CONFIGURATION: senza permesso, durante il caricamento o senza login Microsoft sul
 * product-service (404) la sezione non compare, così il portale di default resta identico.
 */

import { useState } from 'react';
import { ApiError } from '../../api/client.js';
import { CopyButton } from '../../components/CopyButton.js';
import { entraRedirectUri } from '../../auth/index.js';
import { useRolePermissions, useSsoSettings, useUpdateSsoSettings } from './hooks.js';
import type { SsoSettings, SsoSettingsInput } from './sso-api.js';
import styles from './Admin.module.css';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SOURCE_LABEL: Record<SsoSettings['source'], string> = {
  admin: 'impostato da /admin',
  env: 'da variabili SSO_ENTRA_* (seed)',
  default: 'mai configurato',
};

const ROLE_ORDER = ['security_analyst', 'developer', 'analyst_supervisor', 'admin'];

function toInput(s: SsoSettings): SsoSettingsInput {
  return {
    enabled: s.enabled,
    tenantId: s.tenantId,
    clientId: s.clientId,
    roleMappings: s.roleMappings.map((m) => ({ ...m })),
    defaultRole: s.defaultRole,
  };
}

/** Errore di validazione lato client (il server applica le stesse regole), null se valido. */
export function ssoFormError(f: SsoSettingsInput): string | null {
  const tenant = f.tenantId?.trim() ?? '';
  const client = f.clientId?.trim() ?? '';
  if (tenant && !GUID.test(tenant)) return 'Tenant ID: serve il Directory (tenant) ID, un GUID.';
  if (client && !GUID.test(client)) return 'Client ID: serve l’Application (client) ID, un GUID.';
  if (f.roleMappings.some((m) => !m.claim.trim())) return 'Ogni mapping richiede un valore claim.';
  if (f.enabled && (!tenant || !client)) return "Per attivare l'SSO servono Tenant ID e Client ID.";
  if (f.enabled && f.roleMappings.length === 0 && !f.defaultRole) {
    return 'Serve almeno un mapping o un ruolo di default: altrimenti nessuno può entrare.';
  }
  return null;
}

function normalize(f: SsoSettingsInput): SsoSettingsInput {
  return {
    enabled: f.enabled,
    tenantId: f.tenantId?.trim() || null,
    clientId: f.clientId?.trim() || null,
    roleMappings: f.roleMappings.map((m) => ({ claim: m.claim.trim(), role: m.role })),
    defaultRole: f.defaultRole || null,
  };
}

export function SezioneSso({ puoConfigurare }: { puoConfigurare: boolean }) {
  const { data: raw, isLoading, isError, error } = useSsoSettings(puoConfigurare);
  // Risposta non conforme (es. backend senza l'endpoint dietro un proxy): sezione non popolata.
  const data = raw && Array.isArray(raw.roleMappings) ? raw : undefined;
  const { data: rolesMap } = useRolePermissions(puoConfigurare);
  const {
    mutate,
    isPending,
    isError: mutError,
    error: mutErrObj,
    isSuccess,
    reset,
  } = useUpdateSsoSettings();
  const [draft, setDraft] = useState<SsoSettingsInput | null>(null);

  const form = draft ?? (data ? toInput(data) : null);
  const errore = form && draft ? ssoFormError(form) : null;
  const roles = [...new Set([...Object.keys(rolesMap ?? {}), 'admin'])].sort((a, b) => {
    const ia = ROLE_ORDER.indexOf(a);
    const ib = ROLE_ORDER.indexOf(b);
    if (ia >= 0 || ib >= 0) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    return a.localeCompare(b);
  });
  const notAvailable = isError && error instanceof ApiError && error.status === 404;

  function edit(patch: Partial<SsoSettingsInput>) {
    if (!form) return;
    reset();
    setDraft({ ...form, ...patch });
  }

  function editMapping(i: number, patch: Partial<{ claim: string; role: string }>) {
    if (!form) return;
    edit({ roleMappings: form.roleMappings.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  }

  function salva() {
    if (!draft || errore) return;
    mutate(normalize(draft), { onSuccess: () => setDraft(null) });
  }

  // Senza SYSTEM_CONFIGURATION non si può sapere se il login Microsoft è attivo (l'endpoint è solo
  // admin), e con login Microsoft non attivo (product-service senza AUTH_BROKER_ENABLED, es.
  // TC_AUTH_PROVIDER=oidc-mock) la sezione non serve: in entrambi i casi non compare affatto.
  if (!puoConfigurare || isLoading || notAvailable) return null;

  return (
    <div className={styles.section} data-testid="sezione-sso">
      <p className={styles.sectionTitle}>Single Sign-On (Microsoft Entra ID)</p>
      <p className={styles.sectionSubtitle}>
        Gli utenti accedono con il proprio account Microsoft. I ruoli TenaciousC derivano dai{' '}
        <strong>gruppi</strong> (object ID) o dagli <strong>App Role</strong> assegnati in Entra;
        chi non ha alcun mapping riceve il ruolo di default, oppure non entra.
      </p>

      {isError && (
        <div className={styles.errorMsg} data-testid="sso-error">
          {error instanceof ApiError ? error.message : (error?.message ?? 'Errore caricamento')}
        </div>
      )}

      {data && form && (
        <>
          <div className={styles.infoBox} data-testid="sso-setup-help">
            <p>
              <strong>In Entra ID</strong> (App registrations → New registration): piattaforma{' '}
              <strong>Single-page application</strong> con Redirect URI{' '}
              <code data-testid="sso-redirect-uri">{entraRedirectUri()}</code>{' '}
              <CopyButton
                text={entraRedirectUri()}
                label="Copia Redirect URI"
                testId="sso-copy-redirect-uri"
              />
              . Nessun client secret: il portale usa PKCE.
            </p>
            <p>
              Ruoli: definisci App Role (es. <code>TC.Analyst</code>) e assegnali in Enterprise
              applications, oppure abilita il claim <code>groups</code> (Token configuration) e usa
              l&apos;object ID del gruppo.
            </p>
          </div>

          {!data.breakGlassConfigured && (
            <p className={styles.inlineMutError} data-testid="sso-no-break-glass">
              Attenzione: nessun accesso amministratore locale (AUTH_BREAKGLASS_*). Con una
              configurazione errata nessun admin potrebbe rientrare.
            </p>
          )}

          <div className={styles.formRow}>
            <label className={styles.formGroup}>
              <span className={styles.formGroupLabel}>Login Microsoft</span>
              <span>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => edit({ enabled: e.target.checked })}
                  disabled={isPending}
                  data-testid="sso-enabled"
                />{' '}
                attivo
              </span>
            </label>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel} htmlFor="sso-tenant">
                Directory (tenant) ID
              </label>
              <input
                id="sso-tenant"
                className={styles.formSelect}
                style={{ minWidth: '22rem', fontFamily: 'monospace' }}
                value={form.tenantId ?? ''}
                placeholder="00000000-0000-0000-0000-000000000000"
                onChange={(e) => edit({ tenantId: e.target.value })}
                disabled={isPending}
                data-testid="sso-tenant"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel} htmlFor="sso-client">
                Application (client) ID
              </label>
              <input
                id="sso-client"
                className={styles.formSelect}
                style={{ minWidth: '22rem', fontFamily: 'monospace' }}
                value={form.clientId ?? ''}
                placeholder="00000000-0000-0000-0000-000000000000"
                onChange={(e) => edit({ clientId: e.target.value })}
                disabled={isPending}
                data-testid="sso-client"
              />
            </div>
          </div>

          <table className={styles.capabilitiesTable} style={{ marginTop: '1rem' }}>
            <thead>
              <tr>
                <th>Gruppo (object ID) o App Role</th>
                <th>Ruolo TenaciousC</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {form.roleMappings.map((m, i) => (
                <tr key={i} data-testid="sso-mapping-row">
                  <td>
                    <input
                      className={styles.formSelect}
                      style={{ width: '100%', fontFamily: 'monospace' }}
                      value={m.claim}
                      aria-label={`Valore claim, riga ${i + 1}`}
                      onChange={(e) => editMapping(i, { claim: e.target.value })}
                      disabled={isPending}
                      data-testid={`sso-mapping-claim-${i}`}
                    />
                  </td>
                  <td>
                    <select
                      className={styles.formSelect}
                      value={m.role}
                      aria-label={`Ruolo, riga ${i + 1}`}
                      onChange={(e) => editMapping(i, { role: e.target.value })}
                      disabled={isPending}
                      data-testid={`sso-mapping-role-${i}`}
                    >
                      {[...new Set([...roles, m.role])].map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      onClick={() =>
                        edit({ roleMappings: form.roleMappings.filter((_, j) => j !== i) })
                      }
                      disabled={isPending}
                      data-testid={`sso-mapping-remove-${i}`}
                    >
                      Rimuovi
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.formRow}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() =>
                edit({
                  roleMappings: [...form.roleMappings, { claim: '', role: 'security_analyst' }],
                })
              }
              disabled={isPending}
              data-testid="sso-mapping-add"
            >
              Aggiungi mapping
            </button>
            <div className={styles.formGroup}>
              <label className={styles.formGroupLabel} htmlFor="sso-default-role">
                Ruolo di default (utenti senza mapping)
              </label>
              <select
                id="sso-default-role"
                className={styles.formSelect}
                value={form.defaultRole ?? ''}
                onChange={(e) => edit({ defaultRole: e.target.value || null })}
                disabled={isPending}
                data-testid="sso-default-role"
              >
                <option value="">— nessuno: accesso negato —</option>
                {roles
                  .filter((r) => r !== 'admin')
                  .map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={salva}
              disabled={isPending || !draft || Boolean(errore)}
              data-testid="sso-save"
            >
              {isPending ? 'Salvataggio...' : 'Salva'}
            </button>
            {errore && (
              <span className={styles.inlineMutError} role="alert" data-testid="sso-invalid">
                {errore}
              </span>
            )}
            {mutError && (
              <span className={styles.inlineMutError} data-testid="sso-mut-error">
                {mutErrObj instanceof ApiError
                  ? mutErrObj.message
                  : (mutErrObj?.message ?? 'Errore salvataggio')}
              </span>
            )}
            {isSuccess && (
              <span className={styles.successMsg} data-testid="sso-success">
                Impostazioni salvate: valgono dal prossimo login.
              </span>
            )}
          </div>

          <p className={styles.sectionSubtitle} data-testid="sso-current">
            Stato: <strong>{data.enabled ? 'attivo' : 'disattivo'}</strong> (
            {SOURCE_LABEL[data.source]}
            {data.updatedBy ? `, da ${data.updatedBy}` : ''}
            {data.updatedAt ? ` il ${new Date(data.updatedAt).toLocaleString('it-IT')}` : ''}) ·
            scope richiesti: <code>{data.scopes.join(' ')}</code>
          </p>
        </>
      )}
    </div>
  );
}
